import { useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import api from '../../api/client'

const SMP_STEP_LABELS = [
  'Parse SOW', 'Find Fields', 'Azure OpenAI',
  'Discover Fields', 'Chunk & Embed', 'Extract Values',
  'Populate Template', 'Validate',
]

const RAID_STEP_LABELS = [
  'Parse SOW', 'Azure OpenAI', 'Project Name', 'RAID Analysis', 'Populate Tracker',
]

const TVR_STEP_LABELS = [
  'Parse SOW', 'Azure OpenAI', 'Project Name', 'Value Analysis', 'Populate Tracker',
]

const OBLIGATION_STEP_LABELS = [
  'Parse SOW', 'Azure OpenAI', 'Project Name', 'Extract Obligations', 'Populate Tracker',
]

const STM_STEP_LABELS = [
  'Parse SOW', 'Azure OpenAI', 'Project Name', 'Traceability Analysis', 'Populate Matrix',
]

const RTM_STEP_LABELS = [
  'Parse Document', 'Azure OpenAI', 'Project Name', 'Requirements Analysis', 'Populate Matrix',
]

function classifyLog(line) {
  const l = line.toLowerCase()
  if (l.includes('step ') && l.includes('/'))                                              return 'step'
  if (l.includes('error'))                                                                 return 'err'
  if (l.includes('warn'))                                                                  return 'warn'
  if (l.includes('smp generated') || l.includes('raid analysis completed') || l.includes('done!')) return 'done'
  if (line.startsWith('[') && line.includes(']  '))                                        return 'detail'
  return ''
}

function StepBar({ currentStep, stepLabels }) {
  return (
    <div className="c-steps">
      {stepLabels.map((label, i) => {
        const n        = i + 1
        const isDone   = n < currentStep
        const isActive = n === currentStep
        return (
          <div key={n} className={['c-step-item', isDone ? 'done' : '', isActive ? 'active' : ''].filter(Boolean).join(' ')}>
            <div className="c-step-circle">{isDone ? <Check size={12} /> : n}</div>
            <div className="c-step-label">{label}</div>
          </div>
        )
      })}
    </div>
  )
}

export default function ProgressCard({ jobId, jobType = 'smp', onComplete, onFail }) {
  const [jobData, setJobData] = useState({ job_type: jobType, step: 0, step_name: 'Starting…', progress: 0, logs: [] })
  const logPanelRef   = useRef(null)
  const onCompleteRef = useRef(onComplete)
  const onFailRef     = useRef(onFail)

  useEffect(() => { onCompleteRef.current = onComplete; onFailRef.current = onFail })

  useEffect(() => {
    if (logPanelRef.current) logPanelRef.current.scrollTop = logPanelRef.current.scrollHeight
  }, [jobData.logs.length])

  useEffect(() => {
    if (!jobId) return
    let cancelled = false

    async function poll() {
      if (cancelled) return
      try {
        const { data } = await api.get(`/compliance/status/${jobId}`)
        if (!cancelled) setJobData(data)
        if (data.status === 'completed') { cancelled = true; onCompleteRef.current(data) }
        else if (data.status === 'failed') { cancelled = true; onFailRef.current(data.error || 'Pipeline failed.') }
      } catch (_) { /* network blip — keep polling */ }
    }

    poll()
    const timer = setInterval(poll, 2000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [jobId])

  const { job_type, step, step_name, progress, logs } = jobData
  const isRaid = job_type === 'raid'
  const isTvr  = job_type === 'tvr'
    const isObligation = job_type === 'obligation'
  const isStm  = job_type === 'stm'
  const isRtm  = job_type === 'rtm'
  const stepLabels = isRtm ? RTM_STEP_LABELS : isStm ? STM_STEP_LABELS : isObligation ? OBLIGATION_STEP_LABELS : isTvr ? TVR_STEP_LABELS : isRaid ? RAID_STEP_LABELS : SMP_STEP_LABELS
  const title      = isRtm ? 'Extracting Requirements…' : isStm ? 'Identifying Service Traceability Entries…' : isObligation ? 'Extracting Contractual Obligations…' : isTvr ? 'Identifying Value Opportunities…' : isRaid ? 'Running RAID Analysis…' : 'Generating SMP…'
  const desc       = isRtm
    ? `Your requirements document is being processed through the ${RTM_STEP_LABELS.length}-step Requirements Traceability Matrix pipeline.`
    : isStm
      ? `Your SOW is being processed through the ${STM_STEP_LABELS.length}-step Service Traceability Matrix pipeline.`
      : isObligation
        ? `Your SOW is being processed through the ${OBLIGATION_STEP_LABELS.length}-step Obligations pipeline.`
        : isTvr
          ? `Your SOW is being processed through the ${TVR_STEP_LABELS.length}-step TVR pipeline.`
          : isRaid
            ? `Your SOW is being processed through the ${RAID_STEP_LABELS.length}-step RAID pipeline.`
            : `Your SOW is being processed through the ${SMP_STEP_LABELS.length}-step pipeline.`


  return (
    <div className="c-card">
      <div className="c-card-title">
        <span className="c-spinner" />
        &nbsp;{title}
      </div>
      <div className="c-card-desc">{desc}</div>

      <StepBar currentStep={step} stepLabels={stepLabels} />

      <div className="c-progress-wrap">
        <div className="c-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="c-progress-meta">
        <strong>{step_name}</strong>
        <span>{progress}%</span>
      </div>

      <div className="c-log-panel" ref={logPanelRef}>
        {logs.map((line, i) => (
          <div key={i} className={`c-log-line ${classifyLog(line)}`}>{line}</div>
        ))}
      </div>
    </div>
  )
}
